import * as React from 'react';
import { useState } from 'react';
import { ProLayout } from '@ant-design/pro-layout';

import Icon, {
    BarChartOutlined,
    BookOutlined,
    CloudServerOutlined,
    ContactsOutlined,
    ControlOutlined,
    CustomerServiceOutlined,
    DollarCircleOutlined,
    FileTextOutlined,
    FormOutlined,
    HomeOutlined,
    LineChartOutlined,
    MoonFilled,
    PhoneOutlined,
    PictureOutlined,
    PieChartOutlined,
    PlayCircleOutlined,
    PlaySquareOutlined,
    PlusCircleFilled,
    PlusOutlined,
    ProfileOutlined,
    ProjectOutlined,
    RiseOutlined,
    SearchOutlined,
    SettingOutlined,
    SunFilled,
    TeamOutlined,
    ToolOutlined,
    UnorderedListOutlined,
    UserOutlined,
    YoutubeFilled,
    YoutubeOutlined,
} 
from '@ant-design/icons';
import { ProSettings } from '@ant-design/pro-components';
import enUS from 'antd/es/locale/en_US';
// import enUS from "antd/locale/en_US";
import { Avatar, Button, ConfigProvider, Dropdown, message } from 'antd';
import { Link, Outlet, useLocation } from 'react-router-dom';
// import {ReactComponent as LogoCompactTrans} from '../assets/dm_logo_compact_trans.svg';
import { ReactComponent as Logo } from '../assets/dm_logo_long.svg';
import { UserAuth } from '../context/AuthContext';
import { AvatarDropdownMenu } from './AvatarDropdownMenu';
import { Route } from '@ant-design/pro-layout/es/typing';
import { ReactComponent as TikTokSvg } from '../assets/tiktok.svg';
import { useCan, useIsRole } from '../context/auth_atoms';
import axiosApiInstance from '../api/axiosClient';
import { darkAntTheme, darkProLayoutToken } from '../theme/darkTheme';
import { NotificationBell } from './notifications/NotificationBell';
type Props = {};
const userID = localStorage.getItem('userID') || '';
export const MyLayout = (props: Props) => {
    const [settings, setSetting] = useState<Partial<ProSettings> | undefined>({
        fixSiderbar: true,
        layout: 'mix',
        splitMenus: true,
        contentWidth: 'Fluid',
    });

    const [num, setNum] = useState(40);
    const [isDarkMode, setIsDarkMode] = useState(() => {
        return localStorage.getItem('darkMode') === 'true';
    });
    const location = useLocation();

    // Fetch dark mode preference from database on mount
    React.useEffect(() => {
        const fetchDarkMode = async () => {
            try {
                const layout = await axiosApiInstance.get('/api/layout/home-layout/');
                if (layout.data.dark_mode !== undefined && !layout.data.newUser) {
                    const dbDarkMode = layout.data.dark_mode ? true : false;
                    setIsDarkMode(dbDarkMode);
                    localStorage.setItem('darkMode', dbDarkMode.toString());
                }
            } catch (error) {
                // If fetch fails, keep the localStorage value
                console.error('Failed to fetch dark mode preference:', error);
            }
        };
        fetchDarkMode();
    }, []);

    React.useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark-mode');
        } else {
            document.documentElement.classList.remove('dark-mode');
        }
        localStorage.setItem('darkMode', isDarkMode.toString());
    }, [isDarkMode]);

    const toggleDarkMode = () => {
        const next = !isDarkMode;
        setIsDarkMode(next);
        saveDarkMode(next);
    };


    const saveDarkMode = async (darkMode: boolean = isDarkMode) => {
        // console.log('safe preferces hit')
        try {
            await axiosApiInstance.put(`/api/layout/home-layout/`, {
                user_id: userID,
                dark_mode: darkMode ? 1 : 0,
            });
            message.success('Layout saved successfully! ', 3);
        } catch (error) {
            message.error('Failed to save layout preferences.', 3);
            // console.error("Failed to save preferences:", error)
        }
    };


    const { user } = UserAuth();
    const can = useCan();
    const is = useIsRole();

    const managerReportingRoutes = [
        {
            path: '/reporting/engine',
            name: 'Charts Engine',
            icon: <PieChartOutlined />,
        },
    ];

    let logoRoute: Route = {
        path: '/home',
        name: 'Home',
        icon: <HomeOutlined />,
    };
    let accessibleRoutes: Route = {
        path: '/',
        routes: [
            {
                path: '/home',
                name: 'Home',
                icon: <HomeOutlined />,
                // component: './Welcome',
            },
        ],
    };

    let crm = {
        path: '/crm',
        name: 'Customer Management',
        icon: <TeamOutlined />,
        routes: [],
    };

    if (can('read', 'customers::data')) {
        crm.routes.push(
            {
                path: '/crm/customers',
                name: 'Customers',
                icon: <TeamOutlined />,
                routes: [
                    {
                        // path: '/crm/customers',
                        path: '/crm/customers/list',
                        name: 'List',
                        icon: <UnorderedListOutlined />,
                        // exact: true,
                    },
                    {
                        path: '/crm/customers/new',
                        name: 'Create',
                        icon: <FormOutlined />,
                        // exact: true,
                        // computedMatch: {path: ''}
                    },
                    {
                        path: '/crm/customers/view/:id',
                        name: 'Customer',
                        hideInMenu: true,
                    },
                    {
                        path: '/crm/customers/edit/:id',
                        name: 'Edit Customer',
                        hideInMenu: true,
                    },
                ],
            },
            {
                path: '/crm/assets',
                name: 'Assets',
                icon: <RiseOutlined />,
                routes: [
                    {
                        // path: '/crm/assets',
                        path: '/crm/assets/list',
                        name: 'List',
                        icon: <UnorderedListOutlined />,
                    },
                    {
                        path: '/crm/assets/new',
                        name: 'Create',
                        icon: <FormOutlined />,
                    },
                    {
                        path: '/crm/assets/view/:id',
                        name: 'Asset',
                        hideInMenu: true,
                    },
                    {
                        path: '/crm/assets/edit/:id',
                        name: 'Edit Asset',
                        hideInMenu: true,
                    },
                ],
            },
            {
                path: '/crm/contracts',
                name: 'Contracts',
                icon: <FileTextOutlined />,
                routes: [
                    {
                        path: '/crm/contracts/list',
                        name: 'List',
                        icon: <UnorderedListOutlined />,
                    },
                    {
                        path: '/crm/contracts/new',
                        name: 'Create',
                        icon: <FormOutlined />,
                    },
                    {
                        path: '/crm/contracts/view/:id',
                        name: 'Contract',
                        hideInMenu: true,
                    },
                    {
                        path: '/crm/contracts/edit/:id',
                        name: 'Edit Contract',
                        hideInMenu: true,
                    },
                ],
            },
            {
                path: '/crm/leads',
                name: 'Creators Leads',
                icon: <FormOutlined />,
                routes: [
                    {
                        path: '/crm/leads/list',
                        name: 'Manage',
                        icon: <UnorderedListOutlined />,
                    },
                    {
                        path: '/crm/leads/search',
                        name: 'Discover',
                        icon: <SearchOutlined />,
                    },
                ],
            },

            can('read', 'connections::data') && {
                path: '/social/',
                name: 'Social',
                icon: <ContactsOutlined />,
                routes: [
                    {
                        path: '/social/connections',
                        name: 'Connections',
                        icon: <PhoneOutlined />,
                    },
                ],
            },
        );
    }

    if (can('read', 'creators::data')) {
        accessibleRoutes.routes.push({
            path: '/creators',
            name: 'Creators',
            icon: <TeamOutlined />,
            routes: [
                {
                    path: '/creators/list',
                    name: 'Search',
                    icon: <SearchOutlined />,
                    hideInMenu: true,
                },
            ],
        });
        accessibleRoutes.routes.push({
            path: '/creators/campaigns',
            name: 'Campaigns',
            icon: <ProjectOutlined />,
        });
    }

    if (can('read', 'customers::data')) {
        accessibleRoutes.routes.push(crm);
    }

    if (can('read', 'finances::data')) {
        accessibleRoutes.routes.push({
            path: '/erp/paymentcycles',
            name: 'Financials',
            icon: <BookOutlined />,
            routes: [
                {
                    path: '/erp/paymentcycles/:id',
                    name: 'Payment Cycle',
                    hideInMenu: true,
                },
            ],
        });
    }

    if (can('exec', 'reporting::data')) {
        accessibleRoutes.routes.push({
            path: '/reporting',
            name: 'Reporting',
            icon: <BarChartOutlined />,
            routes: [
                {
                    path: '/reporting/smartreports',
                    name: 'Smart Reports',
                    icon: <LineChartOutlined />,
                    routes: [
                        {
                            path: '/reporting/smartreports',
                            name: 'CMS Channels',
                            icon: <DollarCircleOutlined />,
                        },
                        {
                            path: '/reporting/brandaccounts',
                            name: 'Managed Channels',
                            icon: <SearchOutlined />,
                        },
                        can('read', 'testing::features') && {
                            path: '/reporting/tiktok-reports',
                            name: 'TikTok Reports',
                            icon: <Icon component={TikTokSvg} style={{ color: '#6e6e6e', fontSize: 14 }} />,
                        },
                    ].filter(Boolean),
                },
                ...(can('read', 'management::reporting::data') ? managerReportingRoutes : []),
                {
                    path: '/reporting/videocomparison',
                    name: 'Video Comparison',
                    icon: <PlaySquareOutlined />,
                },
                {
                    path: '/reporting/videostats',
                    name: 'Video Statistics',
                    icon: <PlayCircleOutlined />,
                },
                {
                    path: '/reporting/social-metrics',
                    name: 'Social Metrics',
                    icon: <BarChartOutlined />,
                },
                {
                    path: '/reporting/social-metrics/:id',
                    name: 'Social Metrics Report',
                    hideInMenu: true,
                },
                ...(can('read', 'instagram_influencers::data') ? [{
                    path: '/reporting/instagram-influencers',
                    name: 'Instagram Influencers',
                    icon: <PictureOutlined />,

                }, {
                    path: '/reporting/posts-metrics',
                    name: 'Post Metrics',
                    icon: <BarChartOutlined />,
                }] : []),
                {
                    path: '/reporting/tiermetrics',
                    name: 'Tier Metrics',
                    icon: <BarChartOutlined />,
                },
                {
                    path: '/admin',
                    name: 'Admin Dashboard',
                    hideInMenu: true,
                },
            ].filter(Boolean),
        });
    }

    // Benchmarks
    if (can('view', 'benchmarks::overall')) {
        accessibleRoutes.routes.push({
            path: '/benchmarks',
            name: 'Benchmarks',
            icon: <LineChartOutlined />,
            routes: [
                {
                            path: '/benchmarks/youtube',
                            name: 'YouTube',
                            icon: <YoutubeFilled />,
                        },
                        {
                            path: '/benchmarks/tiktok',
                            name: 'TikTok',
                            icon: <Icon component={TikTokSvg} style={{ color: '#6e6e6e', fontSize: 14 }} />,
                        },
                    ],
        });
    }

    // Music Distribution
    if (can('read', 'music::data')) {
        accessibleRoutes.routes.push({
            path: '/music',
            name: 'Music Distribution',
            icon: <CustomerServiceOutlined />,
            routes: [
                {
                    path: '/music/create',
                    name: 'Create New Release',
                    icon: <PlusOutlined />,
                },
                {
                    path: '/music/main',
                    name: 'Releases',
                    icon: <UnorderedListOutlined />,
                },
                {
                    path: '/music/batches',
                    name: 'Batches',
                    icon: <UnorderedListOutlined />,
                },
                {
                    path: '/music/clients',
                    name: 'Clients',
                    icon: <UnorderedListOutlined />,
                },
                {
                    path: '/music/drafts',
                    name: 'Drafts',
                    icon: <ProfileOutlined />,
                },
                {
                    path: '/music/artists',
                    name: 'Artists',
                    icon: <UserOutlined />,
                },
                {
                    path: '/music/labels',
                    name: 'Labels',
                    icon: <ControlOutlined />,
                },
                {
                    path: '/music/asset_labels',
                    name: 'Asset Labels',
                    icon: <YoutubeOutlined />,
                },
                {
                    path: '/music/workflows',
                    name: 'Upload Status',
                    icon: <CloudServerOutlined />,
                },
                {
                    path: '/music/tools',
                    name: 'Tools',
                    icon: <ToolOutlined />,
                },
            ],
        });
    }

    if (can('read', 'admin::data')) {
        accessibleRoutes.routes.push({
            path: '/admin',
            name: 'Admin',
            icon: <SettingOutlined />,
        });
    }

    if (can('read', 'admin::data')) {
        accessibleRoutes.routes.push({
            path: '/tools',
            name: 'Tools',
            icon: <ControlOutlined />,
            routes: [
                {
                    path: '/tools/metadata_converter',
                    name: 'Metadata Converter',
                    icon: <FileTextOutlined />,
                },
                {
                    path: '/tools/performance_reports',
                    name: 'Performance Reports',
                    icon: <BarChartOutlined />,
                },
                {
                    path: '/tools/youtube_copy_playlist',
                    name: 'YouTube Playlist Copy',
                    icon: <YoutubeOutlined />,
                },
            ],
        });
    }

    if (is('Google Auth Verification')) {
        accessibleRoutes = {
            path: '/',
            routes: [
                {
                    path: '/reporting/brandaccounts',
                    name: 'Reports',
                    icon: <SearchOutlined />,
                },
            ],
        };
    }

    if (is('Lidl User')) {
        accessibleRoutes = {
            path: '/',
            routes: [
                {
                    path: '/creators/list',
                    name: 'Creators',
                    icon: <SearchOutlined />,
                },
            ],
        };
    }

    let appList = undefined;
    if (can('read', 'customers::data')) {
        appList = [
            // {
            //     // icon: 'https://gw.alipayobjects.com/zos/rmsportal/KDpgvguMpGfqaHPjicRK.svg',
            //     icon: <></>,
            //     // icon: <LinkOutlined />,
            //     title: 'Main Platform',
            //     desc: 'CRM, ERP and reporting modules',
            //     url: 'http://app.digitalminds-dashboard.com',
            //     target: '_blank',
            // },
            {
                // icon: <LinkOutlined />,
                icon: <></>,
                title: 'Brands',
                desc: 'Sales & Project Management Solution',
                url: 'http://brands.digitalminds-dashboard.com',
                target: '_blank',
            },
            {
                // icon: <LinkOutlined />,
                icon: <></>,
                title: 'Portal',
                desc: 'Customer and Internal Portal',
                url: 'http://portal.digitalminds-dashboard.com',
                target: '_blank',
            },
            {
                // icon: <LinkOutlined />,
                icon: <></>,
                title: 'RBAC',
                desc: 'Role based access management module',
                url: 'http://rbac.digitalminds-dashboard.com',
                target: '_blank',
            },
            {
                // icon: <LinkOutlined />,
                icon: <></>,
                title: 'Bulk Payments',
                desc: 'Paypal bulk payments management module',
                url: 'http://bulk.digitalminds-dashboard.com',
                target: '_blank',
            },
        ];
    }

    return (
        <div
            id='test-pro-layout'
            style={
                {
                    // height: '100vh',
                    // backgroundColor: "#F5F5F5"
                    // color: "#F5F5F5"
                }
            }
        >
            <ConfigProvider
                locale={enUS}
                theme={
                    isDarkMode
                        ? darkAntTheme
                        : {
                            token: {
                                colorBgBase: '#ffffff',
                                colorTextBase: '#000000',
                                colorBgContainer: '#ffffff',
                                colorBgElevated: '#fafafa',
                                colorBgLayout: '#f5f5f5',
                                borderRadius: 10,
                                borderRadiusLG: 12,
                                borderRadiusSM: 8,
                            },
                        }
                }
            >
                <ProLayout
                    location={location}
                    contentWidth='Fluid'
                    navTheme='light'
                    token={isDarkMode ? darkProLayoutToken : undefined}
                    menuItemRender={(item: any, defaultDom: any) => {
                        return <Link to={item.path}> {defaultDom} </Link>;
                    }}
                    subMenuItemRender={(item: any, defaultDom: any) => (
                        <Link to={item.path}> {defaultDom} </Link>
                    )}
                    // location={{
                    //     pathname,
                    // }}
                    route={accessibleRoutes}
                    appList={appList}
                    siderMenuType='sub'
                    menu={{
                        type: 'sub',
                        collapsedShowGroupTitle: true,
                    }}
                    actionsRender={(props) => {
                        if (props.isMobile) return [];
                        return [
                            <NotificationBell
                                key="NotificationBell"
                                style={{
                                    marginInlineEnd: 24,
                                }}
                            />,
                            <Button
                                key='DarkModeSwitch'
                                type='text'
                                icon={isDarkMode ? <SunFilled /> : <MoonFilled />}
                                onClick={toggleDarkMode}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginInlineEnd: 20,
                                    color: isDarkMode ? 'var(--dm-text-secondary, #9b9b9b)' : undefined,
                                }}
                            />,
                            // <Space>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginInlineEnd: 32,
                                }}
                            >
                                <Dropdown overlay={<AvatarDropdownMenu />}>
                                    <Avatar
                                        src={user?.photoURL}
                                        // size={'small'}
                                    />
                                </Dropdown>
                            </div>,

                            // </Space>
                            // <QuestionCircleFilled key="QuestionCircleFilled"/>,
                            // <GithubFilled key="GithubFilled"/>,
                        ];
                    }}
                    menuFooterRender={(props) => {
                        if (props?.collapsed) return undefined;
                        return (
                            <div
                                style={{
                                    textAlign: 'center',
                                    paddingBlockStart: 12,
                                }}
                            >
                                <div>© 2022 Digital Minds</div>
                                {/*<div></div>*/}
                            </div>
                        );
                    }}
                    logo={(
                        <Link to='/home'>
                            <Logo
                                fill={isDarkMode ? '#ececec' : '#0E3748'}
                                width={170}
                                // style={{marginBottom: -20, marginLeft: -8, marginRight: -32}}
                                style={{ marginBottom: -20, marginLeft: -20, marginRight: -4 }}
                            />
                        </Link>
                    )}
                    // title={false}
                    title={false}
                    {...settings}
                >
                    <Outlet />
                </ProLayout>
            </ConfigProvider>
        </div>
    );
};